import {
  ADDRESS_DETAILS_FORM_MAPPER,
  DEMAT_NOMINEE_STATUS_MAPPER,
  MAXIMUM_DEMAT_NOMINEES,
  NOMINEE_PROOF_OF_IDENTITY,
  NOMINEE_RELATIONSHIP,
  PERSONAL_DETAILS_FORM_MAPPER,
} from "../../constants/nominee";
import {
  calculateAge,
  isValidDate,
  isValidMobileNumber,
  isValidNomineeName,
  validateEmail,
} from "../common/validators";
import { ERROR_MESSAGES } from "../../constants/nominee";
import { get, isEmpty, merge } from "lodash-es";

export const validateFields = (formData = {}, keysToCheck = [], additionalInfo = {}, optionalFlags = []) => {
  let canSubmit = true;
  let errorData = {};
  for (let key of keysToCheck) {
    let value = formData[key];
    if (!value && !optionalFlags.includes(key)) {
      if (
        [
          ADDRESS_DETAILS_FORM_MAPPER.frontDoc,
          ADDRESS_DETAILS_FORM_MAPPER.backDoc,
        ].includes(key)
      ) {
        errorData[key] = ERROR_MESSAGES.uploadDocument;
      } else {
        errorData[key] = ERROR_MESSAGES.emptyField;
      }
      canSubmit = false;
    } else {
      switch (key) {
        case PERSONAL_DETAILS_FORM_MAPPER.mobile:
          if (value && !isValidMobileNumber(value)) {
            errorData[key] = `${ERROR_MESSAGES.general} mobile number`;
            canSubmit = false;
          }
          break;
        case PERSONAL_DETAILS_FORM_MAPPER.dob:
          const age = calculateAge(value);
          if (!isValidDate(value)) {
            errorData[key] = `${ERROR_MESSAGES.general} date`;
            canSubmit = false;
          } else if (age < 18 && !formData.isMinor) {
            errorData[key] = ERROR_MESSAGES.minorDob2;
            canSubmit = false;
          } else if (age >= 18 && formData.isMinor) {
            errorData[key] = ERROR_MESSAGES.minorDob1;
            canSubmit = false;
          }
          break;
        case PERSONAL_DETAILS_FORM_MAPPER.share:
          if (value > additionalInfo.availableShare) {
            errorData[key] = ERROR_MESSAGES.share;
            canSubmit = false;
          }
          if (value <= 0) {
            errorData[key] = ERROR_MESSAGES.minShare;
            canSubmit = false;
          }
          break;
        case PERSONAL_DETAILS_FORM_MAPPER.email:
          if (value && !validateEmail(value)) {
            errorData[
              key
            ] = `${ERROR_MESSAGES.general} ${PERSONAL_DETAILS_FORM_MAPPER.email}`;
            canSubmit = false;
          }
          break;
        case PERSONAL_DETAILS_FORM_MAPPER.name:
          const nomineeNameValidationList =
            additionalInfo.nomineeNameValidationList;
          if (!isEmpty(nomineeNameValidationList)) {
            nomineeNameValidationList.forEach((el) => {
              if (!isValidNomineeName(el.name, value)) {
                if (el.isUser) {
                  errorData[key] = ERROR_MESSAGES.nomineeName;
                  canSubmit = false;
                } else {
                  errorData[key] = `${value} nominee already exists`;
                  canSubmit = false;
                }
              }
            });
          }
          if (value.includes("  ")) {
            errorData[key] = ERROR_MESSAGES.consecutiveSpaces;
            canSubmit = false;
          }
          break;
        case PERSONAL_DETAILS_FORM_MAPPER.guardianName:
          if (!isValidNomineeName(formData.name, value)) {
            errorData[key] = ERROR_MESSAGES.guardianName;
            canSubmit = false;
          }
          if (value.includes("  ")) {
            errorData[key] = ERROR_MESSAGES.consecutiveSpaces;
            canSubmit = false;
          }
          break;
        default:
          break;
      }
    }
  }
  return { errorData, canSubmit };
};

export const getNomineePersonalDetails = (nomineeData) => {
  const personalDetails = Object.keys(PERSONAL_DETAILS_FORM_MAPPER);
  let data = {};
  personalDetails.forEach((el) => {
    if (nomineeData[el]) {
      data[el] = nomineeData[el];
    } else {
      data[el] = "";
    }
  });
  return data;
};

export const getNomineeAddressDetails = (nomineeData) => {
  const addressDetails = Object.keys(ADDRESS_DETAILS_FORM_MAPPER);
  let data = {};
  addressDetails.forEach((el) => {
    if (!isEmpty(nomineeData[el])) {
      data[el] = nomineeData[el];
    } else {
      data[el] = "";
    }
  });
  return data;
};

export const getPoiData = (value) => {
  return NOMINEE_PROOF_OF_IDENTITY.find((data) => data.value === value);
};

export const getNomineePayload = (data) => {
  let nominee = {};
  if (data.isDiscarded) {
    nominee.is_discarded = true;
    nominee.share = data.share;
    return { nominee };
  }
  if (data[PERSONAL_DETAILS_FORM_MAPPER.share]) {
    nominee.share = data[PERSONAL_DETAILS_FORM_MAPPER.share];
  }
  const KEY_MAPPER = {
    [PERSONAL_DETAILS_FORM_MAPPER.name]: "full_name",
    [PERSONAL_DETAILS_FORM_MAPPER.guardianName]: "full_name",
    [PERSONAL_DETAILS_FORM_MAPPER.dob]: "dob",
    [PERSONAL_DETAILS_FORM_MAPPER.email]: "email",
    [PERSONAL_DETAILS_FORM_MAPPER.mobile]: "mobile_no",
    [PERSONAL_DETAILS_FORM_MAPPER.relationship]: "relation",
    [PERSONAL_DETAILS_FORM_MAPPER.guardianRelationship]: "relation",
    [PERSONAL_DETAILS_FORM_MAPPER.isMinor]: "is_minor",
    [ADDRESS_DETAILS_FORM_MAPPER.address]: "address",
    [ADDRESS_DETAILS_FORM_MAPPER.city]: "city",
    [ADDRESS_DETAILS_FORM_MAPPER.state]: "state",
    [ADDRESS_DETAILS_FORM_MAPPER.pincode]: "pincode",
    [ADDRESS_DETAILS_FORM_MAPPER.poi]: "doc_type",
    [ADDRESS_DETAILS_FORM_MAPPER.password]: "password",
    [ADDRESS_DETAILS_FORM_MAPPER.guardianAddress]: "address",
    [ADDRESS_DETAILS_FORM_MAPPER.guardianCity]: "city",
    [ADDRESS_DETAILS_FORM_MAPPER.guardianState]: "state",
    [ADDRESS_DETAILS_FORM_MAPPER.guardianPincode]: "pincode",
  };
  const nomineeKeys = Object.keys(data);
  let nomineeMetaKeys = [
    PERSONAL_DETAILS_FORM_MAPPER.name,
    PERSONAL_DETAILS_FORM_MAPPER.dob,
    PERSONAL_DETAILS_FORM_MAPPER.relationship,
    PERSONAL_DETAILS_FORM_MAPPER.isMinor,
    ADDRESS_DETAILS_FORM_MAPPER.address,
    ADDRESS_DETAILS_FORM_MAPPER.city,
    ADDRESS_DETAILS_FORM_MAPPER.state,
    ADDRESS_DETAILS_FORM_MAPPER.pincode,
  ];
  if (!data[PERSONAL_DETAILS_FORM_MAPPER.isMinor]) {
    nomineeMetaKeys = [
      ...nomineeMetaKeys,
      PERSONAL_DETAILS_FORM_MAPPER.mobile,
      PERSONAL_DETAILS_FORM_MAPPER.email,
    ];
  }
  const docKeys = [
    ADDRESS_DETAILS_FORM_MAPPER.password,
    ADDRESS_DETAILS_FORM_MAPPER.poi,
  ];
  const guardianKeys = [
    PERSONAL_DETAILS_FORM_MAPPER.guardianName,
    PERSONAL_DETAILS_FORM_MAPPER.guardianRelationship,
    PERSONAL_DETAILS_FORM_MAPPER.mobile,
    PERSONAL_DETAILS_FORM_MAPPER.email,
    ADDRESS_DETAILS_FORM_MAPPER.guardianAddress,
    ADDRESS_DETAILS_FORM_MAPPER.guardianCity,
    ADDRESS_DETAILS_FORM_MAPPER.guardianState,
    ADDRESS_DETAILS_FORM_MAPPER.guardianPincode,
  ];
  nomineeKeys.forEach((el) => {
    const nomineeMeta = get(nominee, "group_data.group_meta.nominee_meta", {});
    const guardianMeta = get(
      nominee,
      "group_data.group_meta.guardian_meta",
      {}
    );
    const groupDocData = get(nominee, "group_data.group_doc", {});
    if (nomineeMetaKeys.includes(el)) {
      if (!isEmpty(nomineeMeta)) {
        nominee.group_data.group_meta.nominee_meta[KEY_MAPPER[el]] = data[el];
      } else {
        const data1 = {
          group_data: {
            group_meta: {
              nominee_meta: {
                [KEY_MAPPER[el]]: data[el],
              },
            },
          },
        };
        nominee = merge(nominee, data1);
      }
    } else if (docKeys.includes(el) && !isEmpty(data[el])) {
      if (!isEmpty(groupDocData)) {
        nominee.group_data.group_doc[KEY_MAPPER[el]] = data[el];
      } else {
        const data2 = {
          group_data: {
            group_doc: {
              [KEY_MAPPER[el]]: data[el],
            },
          },
        };
        nominee = merge(nominee, data2);
      }
    } else if (
      guardianKeys.includes(el) &&
      data[PERSONAL_DETAILS_FORM_MAPPER.isMinor]
    ) {
      if (!isEmpty(guardianMeta)) {
        nominee.group_data.group_meta.guardian_meta[KEY_MAPPER[el]] = data[el];
      } else {
        const data3 = {
          group_data: {
            group_meta: {
              guardian_meta: {
                [KEY_MAPPER[el]]: data[el],
              },
            },
          },
        };
        nominee = merge(nominee, data3);
      }
    }
  });
  return { nominee };
};

export const getMfNomineeStatus = (mfNominations) => {
  const status = get(mfNominations, "meta_data_status", "init");
  return status;
};

export const getDematNomineeStatus = (equityNominationData) => {
  const nomineeList = getNomineesList(equityNominationData);
  if (isEmpty(nomineeList)) {
    return DEMAT_NOMINEE_STATUS_MAPPER.init;
  }
  const status = get(equityNominationData, "friendly_status", "init");
  return status;
};

export const isDematNomineeStatusInit = (status) => {
  return status === DEMAT_NOMINEE_STATUS_MAPPER.init;
};

export const isDematNomineeStatusRejected = (status) => {
  return status === DEMAT_NOMINEE_STATUS_MAPPER.rejected;
};

export const isDematNomineeStatusApproved = (status) => {
  return status === DEMAT_NOMINEE_STATUS_MAPPER.approved;
};

export const isNomineeStatusRejectedOrApproved = (status) => {
  return (
    isDematNomineeStatusRejected(status) || isDematNomineeStatusApproved(status)
  );
};

export const getTotalShares = (nomineeList = [], status) => {
  if (isNomineeStatusRejectedOrApproved(status)) {
    return 0;
  }
  const totalShares =
    nomineeList.reduce(
      // eslint-disable-next-line
      (sum, data) => (sum += data.share),
      0
    ) || 0;
  return totalShares;
};

export const getAvailableShares = (nomineeList = [], status) => {
  const totalShares = getTotalShares(nomineeList, status);
  const availableShare = (100 - totalShares).toFixed(2);
  return Number(availableShare);
};

export const hideAddAnotherNominee = (nomineeList = []) => {
  return nomineeList.length === MAXIMUM_DEMAT_NOMINEES;
};

export const getAllNominees = (nomineeList = []) => {
  const nominees = nomineeList.map((data) => {
    return getNomineeData(data);
  });
  return nominees;
};

export const getNomineeData = (data) => {
  if (isEmpty(data)) return {};
  const groupData = get(data, "group_data", {}) || {};
  const docMetaData = get(groupData, "group_doc", {}) || {};
  const nomineeMetaData = get(groupData, "group_meta.nominee_meta", {}) || {};
  const guardianMetaData = get(groupData, "group_meta.guardian_meta", {}) || {};
  let nominee = {
    [PERSONAL_DETAILS_FORM_MAPPER.status]: groupData.group_status || "",
    [PERSONAL_DETAILS_FORM_MAPPER.name]: nomineeMetaData.full_name || "",
    [PERSONAL_DETAILS_FORM_MAPPER.dob]: nomineeMetaData.dob || "",
    [PERSONAL_DETAILS_FORM_MAPPER.share]: data.share || "",
    [PERSONAL_DETAILS_FORM_MAPPER.relationship]: nomineeMetaData.relation || "",
    [PERSONAL_DETAILS_FORM_MAPPER.isMinor]: nomineeMetaData.is_minor,
    [PERSONAL_DETAILS_FORM_MAPPER.id]: data.equity_nominee_id,
    [ADDRESS_DETAILS_FORM_MAPPER.poi]: docMetaData.doc_type,
    [ADDRESS_DETAILS_FORM_MAPPER.password]: docMetaData.doc_password,
    [ADDRESS_DETAILS_FORM_MAPPER.address]: nomineeMetaData.address || "",
    [ADDRESS_DETAILS_FORM_MAPPER.city]: nomineeMetaData.city || "",
    [ADDRESS_DETAILS_FORM_MAPPER.state]: nomineeMetaData.state || "",
    [ADDRESS_DETAILS_FORM_MAPPER.pincode]: nomineeMetaData.pincode || "",
  };
  if (nominee.isMinor) {
    nominee = {
      ...nominee,
      [PERSONAL_DETAILS_FORM_MAPPER.guardianName]:
        guardianMetaData.full_name || "",
      [PERSONAL_DETAILS_FORM_MAPPER.guardianRelationship]:
        guardianMetaData.relation,
      [PERSONAL_DETAILS_FORM_MAPPER.email]: guardianMetaData.email || "",
      [PERSONAL_DETAILS_FORM_MAPPER.mobile]: guardianMetaData.mobile_no || "",
      [ADDRESS_DETAILS_FORM_MAPPER.guardianAddress]:
        guardianMetaData.address || "",
      [ADDRESS_DETAILS_FORM_MAPPER.guardianCity]: guardianMetaData.city || "",
      [ADDRESS_DETAILS_FORM_MAPPER.guardianState]: guardianMetaData.state || "",
      [ADDRESS_DETAILS_FORM_MAPPER.guardianPincode]:
        guardianMetaData.pincode || "",
    };
  } else {
    nominee = {
      ...nominee,
      [PERSONAL_DETAILS_FORM_MAPPER.email]: nomineeMetaData.email || "",
      [PERSONAL_DETAILS_FORM_MAPPER.mobile]: nomineeMetaData.mobile_no || "",
    };
  }
  return nominee;
};

export const getNomineeDataById = (nomineeList = [], id) => {
  const nomineeDetails = nomineeList.find((el) => el.equity_nominee_id === id);
  return getNomineeData(nomineeDetails);
};

export const getUpdatedData = (oldData = {}, newData = {}, keysToCheck = [], optionalFlags = []) => {
  let data = {};
  keysToCheck.forEach((el) => {
    if (newData[el] || optionalFlags.includes(el)) {
      if (typeof newData[el] !== "object" && oldData[el] !== newData[el]) {
        data[el] = newData[el];
      } else if (
        typeof newData[el] === "object" &&
        [
          ADDRESS_DETAILS_FORM_MAPPER.frontDoc,
          ADDRESS_DETAILS_FORM_MAPPER.backDoc,
        ].includes(el) &&
        newData.name !== oldData.name
      ) {
        data[el] = newData[el];
      }
    }
  });
  return data;
};

export const getAddress = (data = {}) => {
  return `${data.address}, ${data.city}, ${data.state} ${data.pincode}`;
};

export const getNomineeAddress = (data = {}) => {
  return `${data.address}, ${data.city}, ${data.state} ${data.pincode}`;
};

export const getGuardianAddress = (data = {}) => {
  return `${data.guardianAddress}, ${data.guardianCity}, ${data.guardianState} ${data.guardianPincode}`;
};

export const isNomineeUpdateFlow = (nomineeDetails) => {
  return !!nomineeDetails.id;
};

export const validateDecimalPercentage = (value) => {
  const validNumber = new RegExp(/^\d*(\.\d{0,2})?$/);
  const isValidPercent = value <= 100;
  return validNumber.test(value) && isValidPercent;
};

export const validateAddress = (value) => {
  const pattern = new RegExp(/^[a-zA-Z0-9\s,'-]*$/);
  const isValidAddress = pattern.test(value);
  return isValidAddress;
};

export const getNomineesList = (equityNominationData) => {
  return get(equityNominationData, "eq_nominee_list", []);
};

export const getNomineeNames = (nominationData, id) => {
  const nomineeList = getNomineesList(nominationData);
  let nomineeNames = [];
  nomineeList.forEach((data) => {
    if (data.equity_nominee_id !== id) {
      const name = get(
        data,
        "group_data.group_meta.nominee_meta.full_name",
        ""
      );
      nomineeNames.push({ name, isNominee: true });
    }
  });
  return nomineeNames;
};

export const getCityStateData = (addressData = {}) => {
  const data = {
    city: addressData.city,
    state: addressData.state,
  }
  return data;
}

export const getGuardianCityStateData = (addressData = {}) => {
  const data = {
    guardianCity: addressData.guardianCity,
    guardianState: addressData.guardianState,
  }
  return data;
}

export const getEsignLink = (equityNominationData = {}) => {
  return get(equityNominationData, "esign_request_detail.esign_link", "");
}

export const getDigioDocId = (equityNominationData = {}) => {
  return get(equityNominationData, "esign_request_detail.digio_doc_id", "");
}

export const getShowDematNominee = (kyc = {}) => {
  return kyc && kyc.equity_investment_ready;
};

export const getNomineeRelationshipViewValue = (value = '') => {
  if (isEmpty(value)) {
		return '';
	}
  const relationshipData = NOMINEE_RELATIONSHIP.find((data) => data.value.toLowerCase() === value.toLowerCase()) || {};
  const formatedRelationship = relationshipData.label || value;
  return formatedRelationship;
}
